import { dayjs } from "@thalamos/common";

import { FormSectionSchema } from "../types/FormSection.js";
import {
  IncidentFormStatusSchema,
  IncidentFormTypeSchema,
  type IncidentWorkItemWithContext,
} from "../types/incidentWorkItem.js";
import type { RecordPlaceOfSafetyArrivalMinimumFields } from "../types/workflow/index.js";

/**
 * Earliest arrival across all active places of safety (ISO 8601), or `undefined`
 * when none qualify — the value the Section 136 clock starts from, independent
 * of which POS form was created first (`getFirstPlaceOfSafety` drives the
 * displayed POS instead). Only forms with a real `arrivalDateTime` and
 * `didNotArrive !== "yes"` count.
 */
export function getEarliestPlaceOfSafetyArrivalDateTime(
  incident: IncidentWorkItemWithContext,
): string | undefined {
  const arrivals = incident.forms
    .filter(
      (f) =>
        f.status === IncidentFormStatusSchema.enum.active &&
        f.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
    )
    .map((form) => {
      const arrival = form.sections.find(
        (s) => s.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
      )?.data as RecordPlaceOfSafetyArrivalMinimumFields | null;

      if (arrival?.didNotArrive?.value === "yes") return undefined;
      return arrival?.arrivalDateTime?.value ?? undefined;
    })
    // Drop absent values and any non-parseable timestamps — a malformed arrival
    // must never end up as the (legally significant) detention clock start.
    .filter(
      (value): value is string =>
        value !== undefined && dayjs.utc(value).isValid(),
    );

  if (arrivals.length === 0) return undefined;

  return arrivals.reduce((earliest, current) =>
    dayjs.utc(current).isBefore(dayjs.utc(earliest)) ? current : earliest,
  );
}
